import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import { TertiaryButton, UnderlinedButton } from 'jl-design-system/components/button/Button';
import { ChevronRightOutlined } from 'jl-design-system/components/icons/ChevronRightOutlined';

// Types
import { OrderSummaryProps, OrderSummaryTitleProps } from './OrderSummary.types';
import { ProductProps } from 'types/Product.types';

// Config
import orderSummaryState from './OrderSummary.state';
import { triggerExpandBasketButtonClickedAnalytics } from '@redux/actions/app/appActions';
import { showEditBasketModal } from '@redux/actions/edit-basket/editBasketActions';
import { getFormId } from '@utils/form/configs/itemQuantity';
import { pluraliseItemWord } from '@utils/helpers/pluraliseItem';

// Components
import BodyText from '@components/body-text';
import Container from '@components/container';
import ExpandableContent from '@components/expandable-content';
import Heading from '@components/heading';
import LoadingWrapper from '@components/loading-wrapper';
import OrderItem, { SinglePageOrderItem } from '@components/order-item';
import TradeInDetails from '@components/trade-in-details';
import { useWindowSize } from '@context/window/WindowSizeContext';

// Styles
import styles from './order-summary.scss';

const itemsNeededToEnableExpander = 3;

export const OrderSummaryTitle = ({
  button,
  collapsed = false,
  id,
  isLoading = false,
  isSinglePageCheckoutEnabled = false,
  label,
  showHeading = true,
  tag = 'div',
  totalQuantity = 1,
}: OrderSummaryTitleProps) => {
  const { sidebarIsVisible } = useWindowSize();
  const heading = `(${totalQuantity} ${pluraliseItemWord(totalQuantity === 1)})`;
  const headingType = label === 'Order summary' ? 'xs' : 's';

  return (
    <Container
      borderBottom={isSinglePageCheckoutEnabled ? undefined : collapsed ? undefined : 'grey-15'}
      className={styles.sidebarHeading}
      flex={{ alignItems: 'center', justifyContent: 'space-between' }}
      paddingX={isSinglePageCheckoutEnabled && sidebarIsVisible ? '0' : '3'}
      paddingY="1.5"
      tag={tag}
    >
      <LoadingWrapper isLoading={isLoading} type="dark">
        <Heading className="w-auto" id={id} tag="h2" testId="order-summary-header" type={headingType}>
          {label}
          {showHeading && (
            <>
              {' '}
              <BodyText tag="span">{heading}</BodyText>
            </>
          )}
        </Heading>
      </LoadingWrapper>

      {button && <LoadingWrapper isLoading={isLoading}>{button}</LoadingWrapper>}
    </Container>
  );
};

const OrderSummary = ({
  classNames = {},
  collapsed = false,
  displayHeaders = true,
  form,
  hideChildItems = false,
  hideEditBasket = false,
  hideOutOfStockText = false,
  hideQuantities = false,
  hideSubtotals = false,
  isMultiBatch = false,
  isOrderConfirmation = false,
  items = [],
  modalView = false,
  preventEditing = false,
  showReducedQuantityDetails = false,
  sidebarView = false,
}: OrderSummaryProps) => {
  const dispatch = useDispatch<any>();
  const {
    pathname,
    initPaymentPageCallActive,
    isLoadingLazyComponent,
    putDeliveryAddressApiCallActive,
    proceedingToPayment,
    getBatchesDeliveryMethodsApiCallActive,
    paymentProcessing,
    postAgeVerificationApiCallsActive,
    tradeInQuotes = [],
    showTradeInBoxInOrderConfirmation,
    isShipFromStore,
    isSinglePageCheckoutEnabled,
  } = useSelector(orderSummaryState);
  const elRef = useRef<HTMLDivElement>(null);

  const isLoading = items?.length === 0;
  const useSinglePageContainerStyles =
    isSinglePageCheckoutEnabled && !sidebarView && !isOrderConfirmation && !modalView;

  const handleShowEditBasketModalClick = () => {
    dispatch(showEditBasketModal({ baseRoutePath: pathname }));
  };

  const setInlineHeightStyles = () => {
    const orderSummaryItems = elRef?.current?.querySelectorAll('[data-index^="order-summary-item"]');

    if (orderSummaryItems && !collapsed && !isLoading) {
      if (orderSummaryItems.length >= itemsNeededToEnableExpander) {
        const heightContracted = orderSummaryItems[0].clientHeight + orderSummaryItems[1].clientHeight;
        setContainerHeight(heightContracted);
      } else {
        removeContainerHeight();
      }
    }
  };

  useEffect(() => {
    if (sidebarView) setTimeout(setInlineHeightStyles, 10);
  }, [items]);

  const getContainerElement = () =>
    elRef.current?.querySelector<HTMLDivElement>('[data-testid="order-summary-item-container"]');

  const removeContainerHeight = () => {
    const containerElement = getContainerElement();
    if (containerElement) {
      containerElement.style.removeProperty('height');
    }
  };

  const setContainerHeight = (height: number) => {
    const containerElement = getContainerElement();
    if (containerElement) {
      containerElement.style.height = `${height}px`;
    }
  };

  const disableEditButton = () =>
    initPaymentPageCallActive ||
    putDeliveryAddressApiCallActive ||
    proceedingToPayment ||
    getBatchesDeliveryMethodsApiCallActive ||
    paymentProcessing ||
    postAgeVerificationApiCallsActive;

  let totalQuantity = 0;
  let totalRenderedItems = 0;

  const orderItems = items.map((item: ProductProps, index) => {
    const isItemFabric = item.uom !== '';
    totalRenderedItems += 1;

    if (!isItemFabric) {
      totalQuantity += Number(item.quantity) || 0;
    } else {
      totalQuantity += 1;
    }

    const reducedQuantity = showReducedQuantityDetails ? Number(item?.availability?.stockLevel) : undefined;

    const additionalServicesText = isOrderConfirmation ? (
      <strong className={styles.additionalServicesText}>Additional services</strong>
    ) : (
      <p className={styles.additionalServicesText}>Additional services</p>
    );

    const isLast = index === (items?.length ?? 0) - 1;
    const itemContainerClassnames = cx(styles.itemContainer, classNames.item, {
      [styles.lastItem]: isLast && isSinglePageCheckoutEnabled,
    });
    const hasVisibleChildItems = !hideChildItems && !!item.childItems?.length;
    const itemBorderBottom = isSinglePageCheckoutEnabled ? '' : isLast && isOrderConfirmation ? '' : 'grey-15';
    const OrderItemComponent = isSinglePageCheckoutEnabled ? SinglePageOrderItem : OrderItem;

    return (
      <Container
        key={item.id}
        borderBottom={itemBorderBottom}
        className={itemContainerClassnames}
        data-index={`order-summary-item-${totalQuantity}`}
        marginX={isSinglePageCheckoutEnabled ? '0' : modalView ? '0' : '3'}
        paddingY={isSinglePageCheckoutEnabled ? '1' : '2'}
        testId="order-summary-item"
      >
        <Container marginBottom={hasVisibleChildItems ? '2' : '0'}>
          <OrderItemComponent
            displayImage={!!item.imageUrl}
            hideOutOfStockText={hideOutOfStockText}
            hideQuantities={hideQuantities}
            hideSubtotals={hideSubtotals}
            isOrderConfirmation={isOrderConfirmation}
            modal={modalView}
            reducedQuantity={reducedQuantity}
            removeItem={handleShowEditBasketModalClick}
            sidebar={sidebarView}
            {...item}
          />
        </Container>
        {hasVisibleChildItems &&
          item.childItems.map((childItem: ProductProps, indexChild) => {
            const selectedParentQuantity = form?.[getFormId(item.id)]?.values?.itemQuantity ?? reducedQuantity;
            let childItemReducedQuantity;

            if (selectedParentQuantity && childItem.quantity) {
              childItemReducedQuantity =
                selectedParentQuantity < childItem.quantity ? selectedParentQuantity : undefined;
            }

            totalQuantity += Number(childItem.quantity) || 0;

            const isLastChild = indexChild === (item.childItems?.length ?? 0) - 1;

            return (
              <Container key={childItem.id} marginBottom={isLastChild ? '0' : '2'}>
                {(sidebarView || isOrderConfirmation) && indexChild === 0 && additionalServicesText}
                <OrderItemComponent
                  child
                  displayImage={false}
                  hideQuantities={hideQuantities}
                  hideSubtotals={hideSubtotals}
                  isOrderConfirmation={isOrderConfirmation}
                  modal={modalView}
                  preventEditing={preventEditing}
                  reducedQuantity={childItemReducedQuantity}
                  sidebar={sidebarView}
                  {...childItem}
                />
              </Container>
            );
          })}
      </Container>
    );
  });

  const enableScrollableContainer = sidebarView && totalRenderedItems >= itemsNeededToEnableExpander;

  const itemsContainerClassnames = cx({
    [styles.scrollBar]: enableScrollableContainer,
    [styles.singlePageScrollBar]: enableScrollableContainer && isSinglePageCheckoutEnabled,
  });

  totalQuantity += tradeInQuotes.length;

  const headingId = 'order-summary-heading';
  const headingLabel = isSinglePageCheckoutEnabled ? 'Order summary' : isShipFromStore ? 'Item' : 'Basket';

  const editBasketProps = {
    'aria-describedby': headingId,
    'data-testid': 'edit-basket-button',
    disabled: disableEditButton(),
    onClick: handleShowEditBasketModalClick,
  };

  return (
    <div ref={elRef}>
      <Container
        background={isSinglePageCheckoutEnabled ? undefined : 'white'}
        className={cx(classNames.container, {
          [styles.singlePageContainer]: useSinglePageContainerStyles,
        })}
        flex={{ flexDirection: 'col' }}
        marginBottom={collapsed ? '3' : undefined}
        paddingY={useSinglePageContainerStyles ? '1.5' : undefined}
        tag="section"
        testId="order-summary-container"
        width="full"
      >
        {displayHeaders && (
          <>
            {!isMultiBatch || !isOrderConfirmation ? (
              <OrderSummaryTitle
                button={
                  !hideEditBasket &&
                  (isSinglePageCheckoutEnabled ? (
                    <UnderlinedButton
                      {...editBasketProps}
                      className={styles.editBasketButton}
                      labelText={collapsed ? 'View' : 'Edit'}
                    />
                  ) : (
                    <TertiaryButton
                      {...editBasketProps}
                      className={styles.editBasketButton}
                      icon={ChevronRightOutlined}
                      labelText={collapsed ? 'View' : 'Edit'}
                      size="small"
                      submitting={isLoadingLazyComponent}
                    />
                  ))
                }
                collapsed={collapsed}
                id={headingId}
                isLoading={isLoading}
                isSinglePageCheckoutEnabled={isSinglePageCheckoutEnabled}
                label={headingLabel}
                showHeading={!isShipFromStore}
                tag="header"
                totalQuantity={totalQuantity}
              />
            ) : (
              <Container borderBottom="grey-15" paddingTop="1">
                <></>
              </Container>
            )}
          </>
        )}

        {!collapsed && (
          <>
            {!isMultiBatch && (
              <TradeInDetails
                isSinglePageCheckout={isSinglePageCheckoutEnabled}
                showTradeInBoxInOrderConfirmation={showTradeInBoxInOrderConfirmation}
                tradeInItems={tradeInQuotes}
              />
            )}
            <Container relative>
              <Container
                className={itemsContainerClassnames}
                paddingBottom={enableScrollableContainer ? (isSinglePageCheckoutEnabled ? '1' : '3') : undefined}
                paddingRight={enableScrollableContainer ? '1' : undefined}
                relative
                testId="order-summary-item-container"
                width="full"
              >
                <LoadingWrapper componentType="orderItem" isLoading={isLoading} totalItems={items?.length}>
                  <ExpandableContent
                    classNames={{ button: 'mx-3' }}
                    containerCanBeContracted={!enableScrollableContainer && !modalView}
                    onExpanded={() => dispatch(triggerExpandBasketButtonClickedAnalytics())}
                  >
                    {orderItems}
                  </ExpandableContent>
                </LoadingWrapper>
              </Container>
              {enableScrollableContainer && !modalView && (
                <div
                  className={cx(styles.fade, {
                    [styles.fadeSinglePage]: isSinglePageCheckoutEnabled,
                  })}
                  data-testid="scrollbar-fade"
                />
              )}
            </Container>
          </>
        )}
      </Container>
    </div>
  );
};

export default OrderSummary;
